"use client";
import { postData } from "@/api/api";
import { yupResolver } from "@hookform/resolvers/yup";
import { useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import ErrorMsg from "../common/error-msg";

type FormData = {
  name: string;
  email: string;
  subject: string;
  message: string;
};

const schema = yup.object().shape({
  name: yup.string().required().label("Name"),
  email: yup.string().required().email().label("Email"),
  subject: yup.string().required().min(5).label("Subject"),
  message: yup.string().required().label("Message"),
});

const ContactForm = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>("");

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  const onSubmit = handleSubmit(async (data) => {
    try {
      setLoading(true);
      const resp = await postData(`/contacts`, data);

      if (!resp?.success) {
        setToastMessage(resp?.message);
        return;
      }

      setToastMessage(resp?.message);
      reset();
    } catch (error) {
      setToastMessage(error as string);
    } finally {
      setLoading(false);
      setTimeout(() => setToastMessage(""), 4000);
    }
  });

  return (
    <form onSubmit={onSubmit} className="contact-form-card">
      <div className="form-row">
        <div className="form-col">
          <label>
            Name <span className="required">*</span>
          </label>
          <input
            {...register("name")}
            placeholder="Your Name"
            type="text"
            className={`form-input ${errors.name ? "input-error" : ""}`}
          />
          <ErrorMsg msg={errors.name?.message!} />
        </div>
        <div className="form-col">
          <label>
            Email <span className="required">*</span>
          </label>
          <input
            {...register("email")}
            type="email"
            placeholder="Your Email"
            className={`form-input ${errors.email ? "input-error" : ""}`}
          />
          <ErrorMsg msg={errors.email?.message!} />
        </div>
      </div>

      <div className="form-row">
        <div className="form-col-full">
          <label>
            Subject <span className="required">*</span>
          </label>
          <input
            {...register("subject")}
            type="text"
            placeholder="Subject"
            className={`form-input ${errors.subject ? "input-error" : ""}`}
          />
          <ErrorMsg msg={errors.subject?.message!} />
        </div>
      </div>

      <div className="form-row">
        <div className="form-col-full">
          <label>
            Message <span className="required">*</span>
          </label>
          <textarea
            {...register("message")}
            cols={30}
            rows={6}
            placeholder="Your Message"
            className={`form-textarea ${errors.message ? "input-error" : ""}`}
          ></textarea>
          <ErrorMsg msg={errors.message?.message!} />
        </div>
      </div>

      {toastMessage && (
        <div className="form-toast">{toastMessage}</div>
      )}

      <div className="form-row">
        <div className="form-col-full">
          <button
            type="submit"
            className="form-submit"
            disabled={loading}
          >
            {loading ? "Sending..." : "Send Message"}
          </button>
        </div>
      </div>

      <style>{`
        .contact-form-card {
          background: #fff;
          padding: 2rem;
          border-radius: 1rem;
          box-shadow: 0 8px 20px rgba(0,0,0,0.05);
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }
        .form-row {
          display: flex;
          flex-wrap: wrap;
          gap: 1rem;
        }
        .form-col {
          flex: 1 1 48%;
          display: flex;
          flex-direction: column;
        }
        .form-col-full {
          flex: 1 1 100%;
          display: flex;
          flex-direction: column;
        }
        label {
          font-size: 0.875rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
          color: #111827;
        }
        .required {
          color: #f97316;
        }
        .form-input,
        .form-textarea {
          padding: 0.75rem 1rem;
          border-radius: 0.75rem;
          border: 1px solid #fcf1f1ff;
          font-size: 0.875rem;
          color: #111827;
          outline: none;
          transition: all 0.3s ease;
          width: 100%;
          background-color: #f9fafb;
        }
        .form-input:focus,
        .form-textarea:focus {
          border-color: #ffffffff;
          box-shadow: 0 0 0 3px rgba(233, 110, 10, 0.2);
          background-color: #ffffff;
        }
        .input-error {
          border-color: #dc2626;
        }
        .form-textarea {
          resize: none;
        }
        .form-submit {
          padding: 0.75rem 2rem;
          border-radius: 0.75rem;
          border: none;
          background-color: #000000ff;
          color: #fff;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        .form-submit:hover {
          background-color: #1d4ed8;
          transform: translateY(-2px);
        }
        .form-toast {
          text-align: center;
          background-color: #7fea7f;
          padding: 0.75rem;
          border-radius: 0.75rem;
          font-weight: 600;
          color: #065f06;
          animation: fadeInOut 4s forwards;
        }
        @keyframes fadeInOut {
          0%, 100% { opacity: 0; }
          10%, 90% { opacity: 1; }
        }
        @media (max-width: 768px) {
          .form-col {
            flex: 1 1 100%;
          }
        }
      `}</style>
    </form>
  );
};

export default ContactForm;
